import { atom } from 'jotai';

import {
  DefrostScheduleConfig,
  ScheduleEvent,
  Setting,
  SettingKind,
} from '@repo/sdk/models/components';

import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';

import { equipmentResponseAtom } from './equipment-response.state';

export type DefrostSchedule = {
  settingId: string;
  events: ScheduleEvent[];
};

export type DefrostScheduleValues = {
  defrostMode?: string;
  defrostSchedule?: DefrostSchedule;
};

/**
 * Atom that provides the defrost schedule configuration from the equipment response
 *
 * @remarks
 * Derives defrost schedule configuration from the equipment response atom. Returns undefined if no equipment
 * response is available or if the equipment has no defrost schedule configuration.
 */
export const defrostScheduleConfigAtom = atom<DefrostScheduleConfig | undefined>((get) => {
  const equipment = get(equipmentResponseAtom);

  return equipment?.defrostScheduleConfig;
});
defrostScheduleConfigAtom.debugLabel = 'defrostScheduleConfigAtom';

/**
 * defrostScheduleValuesAtom
 * maintains the values for the defrost schedule
 *
 * @remarks
 * Gets the values for the defrost schedule from the defrost schedule configuration atom
 */
export const defrostScheduleValuesAtom = atom<DefrostScheduleValues | undefined>((get) => {
  const defrostScheduleConfig = get(defrostScheduleConfigAtom);
  if (!defrostScheduleConfig) return undefined;

  const defrostModeValue = defrostScheduleConfig?.defrostModeRef?.id
    ? get(settingsFamily({ settingId: defrostScheduleConfig.defrostModeRef.id } as Setting))
    : undefined;
  const defrostScheduleValue = defrostScheduleConfig?.defrostScheduleRef?.id
    ? get(settingsFamily({ settingId: defrostScheduleConfig.defrostScheduleRef.id } as Setting))
    : undefined;

  const defrostMode = defrostModeValue?.value?.[SettingKind.Enum] || undefined;

  let defrostSchedule: DefrostSchedule | undefined;
  if (defrostScheduleConfig?.defrostScheduleRef?.id) {
    const schedule = defrostScheduleValue?.value?.[SettingKind.Schedule];
    const events = schedule?.events || [];
    defrostSchedule = { settingId: defrostScheduleConfig?.defrostScheduleRef?.id, events };
  }

  return {
    defrostMode,
    defrostSchedule,
  };
});

defrostScheduleValuesAtom.debugLabel = 'defrostScheduleValuesAtom';
